import * as api from "@/api";
import { setValue } from "@/code-editor";
import styles from "@/components/site-list.module.css";
import {
	getUrl,
	readTextFile,
	resetFileList,
	selectLocalFile,
	visitPage,
} from "@/file";
import globals from "@/globals";
import * as signals from "@/signals";
import {
	deleteBackup,
	exportAsZip,
	getDomain,
	getFullDomain,
	importSite,
	newSite,
	openSite,
} from "@/site";
import { Column, SiteContent } from "@/types";
import {
	copyText,
	encodeData,
	handleKeyboardClick,
	isPremium,
	parseJson,
	showContextMenu,
	showDialog,
	showPrompt,
	sleep,
} from "@/utils";
import {
	faChartSimple,
	faFileCode,
	faFileExport,
	faFileImport,
	faFileZipper,
	faFolderOpen,
	faGlobe,
	faLink,
	faPenToSquare,
	faPlus,
	faTrashCan,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { t } from "i18next";
import { JSX, MouseEvent } from "react";

function SiteList(): JSX.Element {
	const isSelected = signals.currentColumn.value === Column.SITE_LIST;
	const sorted = signals.sites.value.map((site) =>
		getFullDomain(decodeURIComponent(site)),
	);
	sorted.sort();

	const clicked = (name: string, event?: MouseEvent): void => {
		if (globals.isMobile) {
			rightClicked(name, event);
		} else {
			void openSite(name);
		}
	};

	const deleteSite = async (fullDomain: string): Promise<void> => {
		const domain = getDomain(fullDomain);
		const encodedDomain = encodeURIComponent(domain);
		const enteredName = await showPrompt(
			t("enterToConfirmDeletion", {
				name: domain,
			}),
		);
		if (!enteredName.toLowerCase().includes(domain)) {
			return;
		}
		signals.isLoadingShown.value = true;
		try {
			const data = await api.deleteSite(encodedDomain);
			if (fullDomain === signals.currentSite.value) {
				document.getElementById("main")?.classList.remove("img");
				setValue();
				signals.currentSite.value = "";
				resetFileList();
				deleteBackup();
			}
			if (data?.alert) {
				void showDialog(data.alert);
				return;
			}
			signals.sites.value = signals.sites.value.filter(
				(site) => site !== encodedDomain,
			);
			void showDialog(fullDomain + t("colon") + t("deletedSuccessfully"));
		} catch (error) {
			void api.handleApiError(error);
		} finally {
			signals.isLoadingShown.value = false;
		}
	};

	const handleNewSiteClick = (): void => {
		newSite();
	};

	const rightClicked = (fullDomain: string, event?: MouseEvent): void => {
		showContextMenu(event, [
			{
				content: {
					icon: faFolderOpen,
					text: t("open"),
				},
				onClick: (): void => {
					void openSite(fullDomain);
				},
				when: globals.isMobile,
			},
			{
				content: {
					icon: faGlobe,
					text: t("visit"),
				},
				onClick: (): void => {
					visitPage("", fullDomain);
				},
				when: true,
			},
			{
				content: {
					icon: faLink,
					text: t("copyLink"),
				},
				onClick: async (): Promise<void> => {
					await copyText(getUrl(false, "", fullDomain));
				},
				when: true,
			},
			{
				content: {
					icon: faPenToSquare,
					text: t("rename"),
				},
				onClick: async (): Promise<void> => {
					await sleep(globals.ANIMATION_WAIT_TIME);
					newSite(getDomain(fullDomain));
				},
				when: true,
			},
			{
				content: {
					icon: faFileExport,
					text: t("export"),
				},
				onClick: async (): Promise<void> => {
					await sleep(globals.ANIMATION_WAIT_TIME * 3);
					showContextMenu(event, [
						{
							content: {
								icon: faFileCode,
								text: "JSON",
							},
							onClick: (): void => {
								if (!signals.login.value.username) {
									throw new Error("Not logged in");
								}
								window.open(
									globals.BACKEND_OVERSEAS +
										"/host/v2/" +
										encodeURIComponent(
											signals.login.value.username,
										) +
										"/site/" +
										encodeURIComponent(
											getDomain(fullDomain),
										) +
										"/content?" +
										encodeData({
											export: "json",
										}),
								);
							},
							when: true,
						},
						{
							content: {
								icon: faFileZipper,
								text: "ZIP",
							},
							onClick: async (): Promise<void> => {
								if (!isPremium()) {
									return;
								}
								if (signals.currentSite.value !== fullDomain) {
									await openSite(fullDomain);
								}
								await sleep(globals.ANIMATION_WAIT_TIME);
								void exportAsZip(fullDomain);
							},
							when: true,
						},
					]);
				},
				when: true,
			},
			{
				content: {
					icon: faFileImport,
					text: t("import"),
				},
				onClick: (): void => {
					selectLocalFile(
						{
							accept: "",
							multiple: false,
						},
						async (files: FileList) => {
							const file = files[0];
							if (file.size > 2097152) {
								void showDialog(
									t("fileCannotLargerThan", {
										size: "2 MB",
									}),
								);
							} else {
								const data = await readTextFile(file);
								void importSite(
									parseJson<SiteContent>(data),
									fullDomain,
								);
							}
						},
					);
				},
				when: true,
			},
			{
				content: {
					icon: faChartSimple,
					text: t("analytics"),
				},
				onClick: async (): Promise<void> => {
					if (!isPremium()) {
						return;
					}
					signals.isLoadingShown.value = true;
					try {
						const data = await api.getAnalytics(
							getDomain(fullDomain),
						);
						if (data.alert || !data.result) {
							if (data.alert) {
								void showDialog(data.alert);
							}
							return;
						}
						const { today, yesterday, month } = data.result;
						void showDialog(
							t("analyticsDetails", {
								monthPv: month.pv,
								monthUv: month.uv,
								todayDesktop: today.desktop,
								todayMobile: today.mobile,
								todayPv: today.pv,
								todayUv: today.uv,
								yesterdayPv: yesterday.pv,
								yesterdayUv: yesterday.uv,
							}),
						);
					} catch (error) {
						void api.handleApiError(error);
					} finally {
						signals.isLoadingShown.value = false;
					}
				},
				when: true,
			},
			{
				content: {
					icon: faTrashCan,
					isDangerous: true,
					text: t("delete"),
				},
				onClick: async (): Promise<void> => {
					await sleep(globals.ANIMATION_WAIT_TIME);
					void deleteSite(fullDomain);
				},
				when: true,
			},
		]);
	};

	const listElements = sorted.map((name) => {
		const handleClick = (event?: MouseEvent): void => {
			clicked(name, event);
		};

		const handleContextMenu = (event: MouseEvent): void => {
			event.preventDefault();
			rightClicked(name, event);
		};

		return (
			<li
				key={name}
				aria-selected={signals.currentSite.value === name}
				onClick={handleClick}
				onContextMenu={handleContextMenu}
				onKeyDown={handleKeyboardClick(handleClick)}
				role="option"
				tabIndex={0}
			>
				{name}
			</li>
		);
	});

	return (
		<div
			aria-hidden={!isSelected}
			className={clsx(
				"list",
				styles["site-list"],
				isSelected && "selected",
			)}
		>
			<div
				aria-haspopup="dialog"
				className="head-button"
				onClick={handleNewSiteClick}
				onKeyDown={handleKeyboardClick(handleNewSiteClick)}
				role="button"
				tabIndex={0}
			>
				<FontAwesomeIcon icon={faPlus} />
				<span>{t("newSite")}</span>
			</div>
			<ul>{listElements}</ul>
		</div>
	);
}

export default SiteList;
